import classNames from 'classnames';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { Typography } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { ISideMenu } from '@/shared/components/molecules/SideMenu/types';
import { UpgradePlanModal } from '@/shared/components/molecules/Subscription/UpgradePlan';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import ArrowUp from '~/images/shared/arrows/arrow-up-line.svg';
import Lock from '~/images/shared/Lock.svg';

const SideMenuNestedRoutes: React.FC<ISideMenu.ISideMenuNestedRoutesProps> = ({
  item,
  openCollapse,
  footer,
}) => {
  const router = useRouter();
  const [isDemoFarm] = useStore((store) => store?.isDemoFarm);
  const { openModal } = useModal();
  const sideRef = useRef<HTMLDivElement>(null);
  const menuItemRef = useRef<HTMLDivElement>(null);
  const [isNestedMenusOpen, setIsNestedMenusOpen] = useState<boolean>(false);
  const isAr = router.locale === 'ar';
  const linkStyles = () =>
    clsx(
      'cursor-pointer rounded-lg tablet:aria-[current=page]:bg-outline-default tablet:hover:bg-outline-default tablet:hover:text-primary-400',
      openCollapse ? 'p-2 w-full' : 'p-1 w-fit mx-auto',
      item.className
    );

  const handleItemClick = () => {
    setIsNestedMenusOpen((isOpen) => !isOpen);
  };

  useEffect(() => {
    const handleOutSideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target === null ||
        sideRef.current?.contains(target) ||
        menuItemRef.current?.contains(target)
      ) {
        return;
      }
      setIsNestedMenusOpen(false);
    };

    document.addEventListener('mousedown', handleOutSideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutSideClick);
    };
  }, [sideRef, isNestedMenusOpen]);

  return (
    <div
      aria-current={
        router.pathname === item.route ||
        (router.pathname.includes(item.route) &&
          router.pathname !== '/' &&
          item.route !== '/')
          ? 'page'
          : false
      }
      className={linkStyles()}
    >
      <div
        className='flex items-center justify-between'
        ref={menuItemRef}
        onClick={handleItemClick}
      >
        <div
          className={classNames(
            'flex w-full items-center aria-[current=false]:text-text-default aria-[current=page]:text-primary-400',
            openCollapse ? 'gap-2' : 'justify-center'
          )}
        >
          <div className='text-primary-400'>{item.icon}</div>
          <Typography
            variant='subtitle2'
            className={clsx('duration-200', !openCollapse && 'hidden')}
          >
            {item.title}
          </Typography>
        </div>
        <span
          className={classNames(
            'px-16 text-primary-default tablet:hidden',
            !isNestedMenusOpen && 'rotate-180'
          )}
        >
          <ArrowUp width='14' height='8' />
        </span>
      </div>
      <div
        className={clsx(
          'top-0 z-30 flex flex-col justify-between py-8 px-16 tablet:fixed tablet:h-screen tablet:w-230 tablet:bg-neutralVariant-95 tablet:py-24',
          openCollapse && !isAr ? 'left-230' : 'left-92',
          openCollapse && isAr ? 'right-230' : 'right-92',
          isNestedMenusOpen ? 'block' : 'hidden'
        )}
        ref={sideRef}
      >
        <div>
          <Typography
            variant='subtitle1'
            className={clsx(
              'py-2 duration-200',
              'hidden tablet:block',
              !openCollapse && 'hidden'
            )}
          >
            {item.title}
          </Typography>
          <div className='flex flex-col gap-2'>
            {item.nestedMenu
              ?.filter((nested) => nested.revealRoute !== false)
              .map(
                (nestedItem, idx) =>
                  nestedItem.customItem ??
                  (!nestedItem.blockedInDemo && nestedItem.clickable ? (
                    <Link key={idx} href={nestedItem.route} legacyBehavior>
                      <a
                        className={clsx(
                          'flex w-full items-center gap-2 rounded-lg p-2',
                          router.pathname === nestedItem.route &&
                            'bg-outline-default text-primary-400'
                        )}
                      >
                        <div className='text-primary-400'>
                          {nestedItem.icon}
                        </div>
                        <Typography
                          className='whitespace-normal'
                          variant='subtitle2'
                          weight='medium'
                        >
                          {nestedItem.title}
                        </Typography>
                      </a>
                    </Link>
                  ) : (
                    <div
                      key={idx}
                      className={clsx(
                        'flex w-full items-center justify-between gap-2 rounded-lg p-2',
                        router.pathname === nestedItem.route &&
                          'bg-outline-default text-primary-400'
                      )}
                      onClick={() => {
                        if (isDemoFarm) {
                          if (nestedItem.blockedInDemo) openModal(<DemoCTA />);
                        } else if (!nestedItem.clickable)
                          openModal(
                            <UpgradePlanModal variant={nestedItem.title} />
                          );
                      }}
                    >
                      <div className='flex items-center gap-2'>
                        <div className='text-primary-400'>
                          {nestedItem.icon}
                        </div>
                        <Typography variant='subtitle2' weight='medium'>
                          {nestedItem.title}
                        </Typography>
                      </div>
                      <div>
                        <Lock />
                      </div>
                    </div>
                  ))
              )}
          </div>
        </div>
        {footer}
      </div>
    </div>
  );
};
export default SideMenuNestedRoutes;
